import { useEffect, useState } from "react";
import {
  Search,
  Eye,
  ShoppingCart,
  CheckCircle,
  Clock,
  IndianRupee,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getAllOrders, updateOrderStatus } from "../api";

function Orders() {
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await getAllOrders();

      if (res.data.success) {
        setOrders(res.data.orders);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const filteredOrders = orders.filter((item) => {
    const orderId = item.orderId?.toLowerCase() || "";

    return orderId.includes(search.toLowerCase());
  });

  const totalRevenue = orders.reduce(
    (sum, order) => sum + (order.amount || 0),
    0,
  );

  const completedOrders = orders.filter(
    (order) => order.status === "Completed",
  ).length;

  const processingOrders = orders.filter(
    (order) =>
      order.status === "Processing" ||
      order.status === "Shipped" ||
      order.status === "Delivered",
  ).length;

  const handleStatusChange = async (orderId, status) => {
    try {
      const res = await updateOrderStatus(orderId, status);

      if (res.data.success) {
        setOrders((prev) =>
          prev.map((order) =>
            order.orderId === orderId
              ? {
                  ...order,
                  status,
                }
              : order,
          ),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
        {/* Total Orders */}
        <div className="bg-white rounded-2xl shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm sm:text-base">Total Orders</p>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-2">
                {orders.length}
              </h2>
            </div>

            <ShoppingCart
              size={28}
              className="text-blue-600 sm:w-8 sm:h-8 md:w-9 md:h-9"
            />
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white rounded-2xl shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm sm:text-base">Completed</p>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-600 mt-2">
                {completedOrders}
              </h2>
            </div>

            <CheckCircle
              size={28}
              className="text-green-600 sm:w-8 sm:h-8 md:w-9 md:h-9"
            />
          </div>
        </div>

        {/* Processing */}
        <div className="bg-white rounded-2xl shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm sm:text-base">Processing</p>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mt-2">
                {processingOrders}
              </h2>
            </div>

            <Clock
              size={28}
              className="text-blue-600 sm:w-8 sm:h-8 md:w-9 md:h-9"
            />
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-2xl shadow p-4 sm:p-5 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm sm:text-base">Revenue</p>

              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mt-2 break-words">
                ₹{totalRevenue.toLocaleString()}
              </h2>
            </div>

            <IndianRupee size={28} className="sm:w-8 sm:h-8 md:w-9 md:h-9" />
          </div>
        </div>
      </div>

      {/* Orders */}
      <div className="bg-white rounded-2xl shadow p-3 md:p-6">
       <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
  {/* Heading */}
  <div>
    <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
      Orders
    </h2>

    <p className="text-sm sm:text-base text-gray-500 mt-1">
      Manage Hydroponic Orders
    </p>
  </div>

  {/* Search */}
  <div className="relative w-full md:w-80 lg:w-96">
    <Search
      size={18}
      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
    />

    <input
      type="text"
      placeholder="Search Order ID..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className="w-full border rounded-xl pl-11 pr-4 py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
</div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-[1000px] w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-4 text-left whitespace-nowrap">Order ID</th>
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Products
                </th>
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Amount
                </th>
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Payment
                </th>
                <th className="px-4 py-3 text-left whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 text-left whitespace-nowrap">Date</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order) => (
                <tr
                  key={order._id}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td className="p-4 font-semibold whitespace-nowrap">
                    {order.orderId}
                  </td>

                  <td className="px-4">
                    <div className="min-w-[220px]">
                      {order.items
                        ?.slice(0, 2)
                        .map((item) => item.title)
                        .join(", ")}

                      {order.items?.length > 2 &&
                        ` +${order.items.length - 2} more`}
                    </div>
                  </td>

                  <td className="px-4 font-medium whitespace-nowrap">
                    ₹{order.amount?.toLocaleString()}
                  </td>

                  <td className="px-4">
                    <span
                      className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                        order.paymentStatus === "Paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {order.paymentStatus}
                    </span>
                  </td>

                  <td className="px-4">
                    <select
                      value={order.status}
                      onChange={(e) =>
                        handleStatusChange(order.orderId, e.target.value)
                      }
                      className={`w-[150px] px-3 py-2 rounded-lg border text-sm font-medium ${
                        order.status === "Completed"
                          ? "bg-green-100 text-green-700"
                          : order.status === "Processing"
                            ? "bg-blue-100 text-blue-700"
                            : order.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                      }`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>

                  <td className="px-4 whitespace-nowrap">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="px-4">
                    <button
                      onClick={() =>
                        navigate(`/dashboard/orders/${order.orderId}`)
                      }
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredOrders.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              No Orders Found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Orders;
